/**
 * Marqueurs d'environnement pour l'onglet du navigateur (titre + favicon).
 *
 * L'environnement est déduit du hostname et non du build : un build production
 * servi en local reste identifié comme local, ce qui est justement le cas d'usage.
 */
export type EnvironmentKind = 'local' | 'dev' | 'prod';

export function getEnvironmentKind(hostname: string = window.location.hostname): EnvironmentKind {
    if (hostname === 'localhost' || hostname === '127.0.0.1' || hostname === '[::1]') {
        return 'local';
    }

    if (hostname.includes('dev') || hostname.includes('staging') || hostname.includes('recette')) {
        return 'dev';
    }

    return 'prod';
}

/** Emoji identifiant l'environnement dans le favicon. */
const ENVIRONMENT_EMOJI: Record<Exclude<EnvironmentKind, 'prod'>, string> = {
    local: '🚧',
    dev: '🧪',
};

/**
 * Préfixe de titre : vide en production, marqueur textuel ailleurs.
 * Volontairement sans emoji, celui-ci étant déjà porté par le favicon.
 */
export function getEnvironmentTitlePrefix(kind: EnvironmentKind): string {
    switch (kind) {
        case 'local':
            return 'LOCAL · ';
        case 'dev':
            return 'DEV · ';
        default:
            return '';
    }
}

/**
 * Favicon de substitution hors production : l'emoji d'environnement rendu en SVG,
 * volontairement très éloigné du favicon de marque pour être repérable dans une barre
 * d'onglets chargée. Retourne `null` en production (on garde le favicon de marque).
 */
export function getEnvironmentFavicon(
    kind: EnvironmentKind,
    variant: 'default' | 'notification' = 'default'
): string | null {
    if (kind === 'prod') {
        return null;
    }

    // Pastille rouge d'angle : même sémantique que le favicon de notification de marque.
    const notificationDot = variant === 'notification'
        ? '<circle cx="25" cy="7" r="6" fill="#ef4444" stroke="#ffffff" stroke-width="2"/>'
        : '';

    const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 32 32">`
        + `<text x="16" y="25" text-anchor="middle" font-size="27">${ENVIRONMENT_EMOJI[kind]}</text>`
        + notificationDot
        + `</svg>`;

    return `data:image/svg+xml,${encodeURIComponent(svg)}`;
}
